import dayjs from 'dayjs';
import { SortDirection } from '~/modules/covenants/types.ts';
import type {
  TBuildManagerApplicationsUrlSearchParams,
  TManagerApplicationSortKey,
  TManagerApplicationsUrlSyncedFilters,
  TManagerApplicationsUrlState,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import {
  DEFAULT_MANAGER_APPLICATION_FILTERS,
  MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS,
  MANAGER_APPLICATION_SORTABLE_COLUMNS,
  MANAGER_APPLICATIONS_URL_MAX_SEARCH_LENGTH,
  MANAGER_APPLICATIONS_URL_PARAMS,
  MANAGER_ASSIGNEE_COLUMN,
  MANAGER_STATUS_COLUMN,
  MANAGER_STATUS_OPTIONS,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import type { TSupervisorApplicationChildPeriod } from '~/store/covenants/types.ts';

const MANAGER_ALLOWED_SORT_KEYS: TManagerApplicationSortKey[] = [
  ...MANAGER_APPLICATION_SORTABLE_COLUMNS.map((column) => column.sortKey),
  MANAGER_STATUS_COLUMN.sortKey,
  MANAGER_ASSIGNEE_COLUMN.sortKey,
];
const MANAGER_ALLOWED_STATUSES = new Set<string>(MANAGER_STATUS_OPTIONS);
const MANAGER_ALLOWED_CHILD_PERIODS = new Set<TSupervisorApplicationChildPeriod>([
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
  'Q1',
  'Q2',
  'Q3',
  'Q4',
]);

const parseNonNegativeNumber = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);

  return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
};

const parseManagerDateFromQuery = (value: string | null, mode: 'start' | 'end') => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const parsedDate = dayjs(value);

  if (!parsedDate.isValid() || parsedDate.format('YYYY-MM-DD') !== value) {
    return null;
  }

  return mode === 'start' ? parsedDate.startOf('day') : parsedDate.endOf('day');
};

const parseManagerSortKeyFromQuery = (
  value: string | null
): TManagerApplicationsUrlSyncedFilters['sortKey'] => {
  if (!value) {
    return null;
  }

  return MANAGER_ALLOWED_SORT_KEYS.includes(value as TManagerApplicationSortKey)
    ? (value as TManagerApplicationSortKey)
    : null;
};

const parseManagerSortDirectionFromQuery = (
  value: string | null
): TManagerApplicationsUrlSyncedFilters['sortDirection'] | null => {
  if (
    value === SortDirection.None ||
    value === SortDirection.Asc ||
    value === SortDirection.Desc
  ) {
    return value;
  }

  return null;
};

const normalizeUniqueStrings = (values: string[]): string[] => {
  const uniqueValues = new Set<string>();

  values.forEach((value) => {
    const normalizedValue = value.trim();

    if (!normalizedValue || uniqueValues.has(normalizedValue)) {
      return;
    }

    uniqueValues.add(normalizedValue);
  });

  return [...uniqueValues];
};

const normalizeStatusFilter = (
  values: string[]
): TManagerApplicationsUrlSyncedFilters['status'] =>
  normalizeUniqueStrings(values).filter(
    (status): status is TManagerApplicationsUrlSyncedFilters['status'][number] =>
      MANAGER_ALLOWED_STATUSES.has(status)
  );

const normalizeAssigneeFilter = (
  values: string[]
): TManagerApplicationsUrlSyncedFilters['assignee'] => normalizeUniqueStrings(values);

const normalizeSegmentFilter = (
  values: string[]
): TManagerApplicationsUrlSyncedFilters['segmentIds'] => normalizeUniqueStrings(values);

const normalizeDocumentIds = (values: string[]): string[] => normalizeUniqueStrings(values);

const normalizeYearPeriod = (value: string | null | undefined): string => {
  const normalizedValue = value?.trim() ?? '';

  return /^\d{4}$/.test(normalizedValue) ? normalizedValue : '';
};

const normalizeChildPeriod = (
  value: string | null
): TSupervisorApplicationChildPeriod | null =>
  value && MANAGER_ALLOWED_CHILD_PERIODS.has(value as TSupervisorApplicationChildPeriod)
    ? (value as TSupervisorApplicationChildPeriod)
    : null;

const resolveManagerSortStateFromQuery = (searchParams: URLSearchParams) => {
  const parsedSortKey = parseManagerSortKeyFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.sortKey)
  );
  const parsedSortDirection = parseManagerSortDirectionFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection)
  );

  if (parsedSortDirection === SortDirection.None && !parsedSortKey) {
    return {
      sortKey: null,
      sortDirection: SortDirection.None,
    };
  }

  if (parsedSortKey) {
    return {
      sortKey: parsedSortKey,
      sortDirection: parsedSortDirection ?? DEFAULT_MANAGER_APPLICATION_FILTERS.sortDirection,
    };
  }

  return {
    sortKey: DEFAULT_MANAGER_APPLICATION_FILTERS.sortKey,
    sortDirection: parsedSortDirection ?? DEFAULT_MANAGER_APPLICATION_FILTERS.sortDirection,
  };
};

const getUrlSafeSearchQuery = (value: string) =>
  value.trim().slice(0, MANAGER_APPLICATIONS_URL_MAX_SEARCH_LENGTH);

const replaceMultiValueParam = (searchParams: URLSearchParams, key: string, values: string[]) => {
  searchParams.delete(key);
  values.forEach((value) => {
    searchParams.append(key, value);
  });
};

export const parseManagerApplicationsUrlState = (search: string): TManagerApplicationsUrlState => {
  const searchParams = new URLSearchParams(search);
  const parsedPage = parseNonNegativeNumber(searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.page));
  const parsedRowsPerPage = parseNonNegativeNumber(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.pageSize)
  );
  const parsedSearchQuery = getUrlSafeSearchQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.search) ?? ''
  );
  const parsedStartDate = parseManagerDateFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.fromDate),
    'start'
  );
  const parsedEndDate = parseManagerDateFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.toDate),
    'end'
  );
  const parsedStatus = normalizeStatusFilter(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.status)
  );
  const parsedAssignee = normalizeAssigneeFilter(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.assignee)
  );
  const parsedSegmentIds = normalizeSegmentFilter(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.segmentIds)
  );
  const parsedDocumentIds = normalizeDocumentIds(
    searchParams.getAll(MANAGER_APPLICATIONS_URL_PARAMS.documentIds)
  );
  const parsedYearPeriod = normalizeYearPeriod(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.yearPeriod)
  );
  const parsedChildPeriod = normalizeChildPeriod(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.childPeriod)
  );
  const parsedTermStartDate = parseManagerDateFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.termStartDate),
    'start'
  );
  const parsedTermEndDate = parseManagerDateFromQuery(
    searchParams.get(MANAGER_APPLICATIONS_URL_PARAMS.termEndDate),
    'end'
  );
  const sortStateFromQuery = resolveManagerSortStateFromQuery(searchParams);
  const isRowsPerPageAllowed = MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS.includes(
    parsedRowsPerPage ?? -1
  );
  const normalizedStartDate = parsedStartDate;
  let normalizedEndDate = parsedEndDate;
  const normalizedTermStartDate = parsedTermStartDate;
  let normalizedTermEndDate = parsedTermEndDate;

  if (normalizedStartDate && normalizedEndDate && normalizedStartDate.isAfter(normalizedEndDate)) {
    normalizedEndDate = normalizedStartDate.endOf('day');
  }

  if (
    normalizedTermStartDate &&
    normalizedTermEndDate &&
    normalizedTermStartDate.isAfter(normalizedTermEndDate)
  ) {
    normalizedTermEndDate = normalizedTermStartDate.endOf('day');
  }

  return {
    page: parsedPage ?? DEFAULT_MANAGER_APPLICATION_FILTERS.page,
    rowsPerPage: isRowsPerPageAllowed
      ? (parsedRowsPerPage as number)
      : DEFAULT_MANAGER_APPLICATION_FILTERS.rowsPerPage,
    searchQuery: parsedSearchQuery,
    startDate: normalizedStartDate,
    endDate: normalizedEndDate,
    sortKey: sortStateFromQuery.sortKey,
    sortDirection: sortStateFromQuery.sortDirection,
    status: parsedStatus,
    assignee: parsedAssignee,
    segmentIds: parsedSegmentIds,
    documentIds: parsedDocumentIds,
    yearPeriod: parsedYearPeriod,
    childPeriod: parsedChildPeriod,
    termStartDate: normalizedTermStartDate,
    termEndDate: normalizedTermEndDate,
  };
};

export const buildManagerApplicationsUrlSearch = ({
  currentSearch,
  page,
  rowsPerPage,
  submittedSearchQuery,
  startDate,
  endDate,
  sortKey,
  sortDirection,
  status,
  assignee,
  segmentIds,
  documentIds,
  yearPeriod,
  childPeriod,
  termStartDate,
  termEndDate,
}: TBuildManagerApplicationsUrlSearchParams) => {
  const searchParams = new URLSearchParams(currentSearch);
  const normalizedSearchQuery = getUrlSafeSearchQuery(submittedSearchQuery);
  const pageValue = String(Math.max(0, page));
  const rowsPerPageValue = String(rowsPerPage);
  const fromDate = startDate?.format('YYYY-MM-DD') ?? '';
  const toDate = endDate?.format('YYYY-MM-DD') ?? '';
  const normalizedStatus = normalizeStatusFilter(status);
  const normalizedAssignee = normalizeAssigneeFilter(assignee);
  const normalizedSegmentIds = normalizeSegmentFilter(segmentIds);
  const normalizedDocumentIds = normalizeDocumentIds(documentIds);
  const normalizedYearPeriod = normalizeYearPeriod(yearPeriod);
  const normalizedChildPeriod = normalizeChildPeriod(childPeriod);
  const termStartDateValue = termStartDate?.format('YYYY-MM-DD') ?? '';
  const termEndDateValue = termEndDate?.format('YYYY-MM-DD') ?? '';

  if (page > 0) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.page, pageValue);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.page);
  }

  if (rowsPerPage !== DEFAULT_MANAGER_APPLICATION_FILTERS.rowsPerPage) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.pageSize, rowsPerPageValue);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.pageSize);
  }

  if (normalizedSearchQuery) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.search, normalizedSearchQuery);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.search);
  }

  if (fromDate) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.fromDate, fromDate);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.fromDate);
  }

  if (toDate) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.toDate, toDate);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.toDate);
  }

  if (sortKey && sortKey !== DEFAULT_MANAGER_APPLICATION_FILTERS.sortKey) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.sortKey, sortKey);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.sortKey);
  }

  if (sortDirection === SortDirection.None) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection, sortDirection);
  } else if (sortDirection !== DEFAULT_MANAGER_APPLICATION_FILTERS.sortDirection) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection, sortDirection);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.sortDirection);
  }

  if (normalizedStatus.length) {
    replaceMultiValueParam(searchParams, MANAGER_APPLICATIONS_URL_PARAMS.status, normalizedStatus);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.status);
  }

  if (normalizedAssignee.length) {
    replaceMultiValueParam(
      searchParams,
      MANAGER_APPLICATIONS_URL_PARAMS.assignee,
      normalizedAssignee
    );
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.assignee);
  }

  if (normalizedSegmentIds.length) {
    replaceMultiValueParam(
      searchParams,
      MANAGER_APPLICATIONS_URL_PARAMS.segmentIds,
      normalizedSegmentIds
    );
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.segmentIds);
  }

  if (normalizedDocumentIds.length) {
    replaceMultiValueParam(
      searchParams,
      MANAGER_APPLICATIONS_URL_PARAMS.documentIds,
      normalizedDocumentIds
    );
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.documentIds);
  }

  if (normalizedYearPeriod) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.yearPeriod, normalizedYearPeriod);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.yearPeriod);
  }

  if (normalizedChildPeriod) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.childPeriod, normalizedChildPeriod);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.childPeriod);
  }

  if (termStartDateValue) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.termStartDate, termStartDateValue);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.termStartDate);
  }

  if (termEndDateValue) {
    searchParams.set(MANAGER_APPLICATIONS_URL_PARAMS.termEndDate, termEndDateValue);
  } else {
    searchParams.delete(MANAGER_APPLICATIONS_URL_PARAMS.termEndDate);
  }

  return searchParams.toString();
};

export const getManagerApplicationsSearchQueryForUrl = getUrlSafeSearchQuery;
